import React, { useState, useEffect } from 'react';
import { HighlightsCarousel } from '@/components/ui/HighlightsCarousel';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface User {
  userId: string;
  _id?: string;
  profilePic?: string;
  name: string;
  order?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
  address?: string;
  primarySkills?: string[];
  secondarySkills?: string[];
  experience?: string;
  designation?: string;
  company?: string;
  // Additional fields for aspirants
  degree?: string;
  fieldOfStudy?: string;
}

interface CarouselSettings {
  speed: number;
  direction: 'left' | 'right';
  focusCardSize: number;
  normalCardSize: number;
}

interface HighlightsData {
  aspirants: {
    users: User[];
    settings: CarouselSettings;
  } | null;
  mentors: {
    users: User[];
    settings: CarouselSettings;
  } | null;
}

export default function Highlights() {
  const [highlightsData, setHighlightsData] = useState<HighlightsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadHighlights();
  }, []);

  const loadHighlights = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/highlights');
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.success) {
          console.log('Highlights page: Received data:', data);
          console.log('Highlights page: Aspirants users:', data.data.aspirants?.users?.length || 0);
          console.log('Highlights page: Mentors users:', data.data.mentors?.users?.length || 0);
          setHighlightsData(data.data);
        } else {
          setError('Failed to load highlights');
        }
      } else {
        setError('Failed to load highlights');
      }
    } catch (error) {
      console.error('Error loading highlights:', error);
      setError('Failed to load highlights');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Loading highlights...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={loadHighlights}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!highlightsData || (!highlightsData.aspirants && !highlightsData.mentors)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">No highlights available at the moment.</p>
        </div>
      </div>
    );
  }

  console.log('Highlights page: Rendering with data:', highlightsData);
  console.log('Highlights page: Aspirants condition:', highlightsData?.aspirants && highlightsData.aspirants.users.length > 0);
  console.log('Highlights page: Mentors condition:', highlightsData?.mentors && highlightsData.mentors.users.length > 0);

    return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-12">
        
        <div className="space-y-12">
          {/* Aspirants Section */}
          {highlightsData.aspirants && highlightsData.aspirants.users.length > 0 && (
            <HighlightsCarousel
              users={highlightsData.aspirants.users}
              settings={highlightsData.aspirants.settings}
              title="Aspirants"
              className="bg-white rounded-lg shadow-sm"
            />
          )}
          
          {/* Mentors Section */}
          {highlightsData.mentors && highlightsData.mentors.users.length > 0 && (
            <HighlightsCarousel
              users={highlightsData.mentors.users}
              settings={highlightsData.mentors.settings}
              title="Mentors"
              className="bg-white rounded-lg shadow-sm"
            />
          )}
        </div>
      </div>
    </div>
  );
}
